/**
 * **Eine Internetadresse ablegen** (Auftraggeber 5.10.2026): Das Symbol links neben der Adresse in der
 * Adresszeile des Browsers, ein Link, ein Lesezeichen oder eine Internetverknüpfung lässt sich auf den
 * Menüpunkt „Geschäftspartner", auf dessen Seite oder auf „Geschäftspartner anlegen" ziehen. Die Seite prüft
 * dann, ob es den Partner schon gibt, und öffnet sonst den ausgefüllten Anlage-Dialog.
 *
 * Liegt in `lib/`, weil das Layout (Menüpunkt) und das Feature (Seite) es teilen — das Layout importiert
 * kein Feature. Der Menüpunkt legt die Adresse in den Speicher und wechselt zur Seite; die Seite holt sie ab.
 *
 * Ein Tab oder ein Fenster als Ganzes lässt sich nicht ablegen: Das Verschieben von Tabs behält der Browser
 * für sich, die Seite erfährt davon nichts.
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from './auth.store'

// ── Was ist eine Adresse? ───────────────────────────────────────────────────

const ADRESSE = /^(https?:\/\/)?([a-z0-9äöü-]+\.)+[a-zäöü]{2,}(:\d+)?([/?#]\S*)?$/i

/** Eine Webadresse in Normalform (`https://…`) — oder `null`, wenn der Text keine ist. */
export function alsAdresse(text: string | null | undefined): string | null {
  const roh = (text ?? '').trim()
  if (!roh || /\s/.test(roh) || !ADRESSE.test(roh)) return null
  try {
    const url = new URL(/^https?:\/\//i.test(roh) ? roh : `https://${roh}`)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

/** Aus dem, was ein Browser beim Ziehen mitgibt: zuerst die Adressliste, dann Firefox' eigenes Format, zuletzt reiner Text. */
export function adresseAusText(daten: { uriList?: string; mozUrl?: string; text?: string }): string | null {
  const ausListe = (daten.uriList ?? '')
    .split(/\r?\n/)
    .map((z) => z.trim())
    .find((z) => z && !z.startsWith('#'))
  return alsAdresse(ausListe) ?? alsAdresse((daten.mozUrl ?? '').split(/\r?\n/)[0]) ?? alsAdresse(daten.text)
}

/** Aus einer Internetverknüpfung: Windows (`.url`, Zeile `URL=…`) oder macOS (`.webloc`, `<string>…</string>`). */
export function adresseAusVerknuepfung(inhalt: string): string | null {
  return alsAdresse(/^\s*URL\s*=\s*(\S+)/im.exec(inhalt)?.[1]) ?? alsAdresse(/<string>\s*(https?:\/\/[^<\s]+)\s*<\/string>/i.exec(inhalt)?.[1])
}

/** Der Text einer kleinen Datei — über den `FileReader`, den jede Umgebung kennt. */
const liesDatei = (datei: File) =>
  new Promise<string>((fertig) => {
    const leser = new FileReader()
    leser.onload = () => fertig(typeof leser.result === 'string' ? leser.result : '')
    leser.onerror = () => fertig('')
    leser.readAsText(datei)
  })

/** Die abgelegte Adresse — `null`, wenn nichts davon eine ist. Die eigene Anwendung zählt nicht. */
export async function adresseAusAblage(dt: DataTransfer): Promise<string | null> {
  // Die Texte sofort lesen: Nach dem Ereignis gibt der Browser sie nicht mehr her.
  let adresse = adresseAusText({ uriList: dt.getData('text/uri-list'), mozUrl: dt.getData('text/x-moz-url'), text: dt.getData('text/plain') })
  if (!adresse) {
    const datei = Array.from(dt.files).find((f) => /\.(url|website|webloc)$/i.test(f.name) && f.size < 64 * 1024)
    if (datei) adresse = adresseAusVerknuepfung(await liesDatei(datei))
  }
  return adresse && new URL(adresse).origin !== window.location.origin ? adresse : null
}

// ── Ziehen erkennen ─────────────────────────────────────────────────────────

/** Läuft gerade ein Ziehen, das **in dieser Anwendung** begonnen hat (Spalte, Zeile, eigener Link)? */
let intern = false

const typen = (dt: DataTransfer | null) => Array.from(dt?.types ?? [])
const traegtAdresse = (dt: DataTransfer | null) => typen(dt).some((t) => t === 'text/uri-list' || t === 'text/x-moz-url')
/** Felder nehmen abgelegten Text selbst an — dort mischt sich die Ablage nicht ein. */
const imFeld = (ziel: EventTarget | null) => ziel instanceof Element && !!ziel.closest('input, textarea, select, [contenteditable="true"]')

/**
 * Kann das Gezogene eine Internetadresse sein? `adresse`: der Browser sagt es (Adresszeile, Link, Lesezeichen);
 * `vielleicht`: reiner Text oder eine Datei — ob es eine Adresse oder Verknüpfung ist, zeigt erst das Ablegen.
 */
export function ziehtAdresse(dt: DataTransfer | null): 'adresse' | 'vielleicht' | null {
  if (intern) return null
  if (traegtAdresse(dt)) return 'adresse'
  return typen(dt).some((t) => t === 'Files' || t === 'text/plain') ? 'vielleicht' : null
}

/**
 * Eigenschaften einer Ablagefläche für Internetadressen. `setAktiv` zeigt, dass eine Adresse darüber schwebt;
 * `ablegen` bekommt die Adresse — oder `null`, wenn etwas abgelegt wurde, das keine ist (dann sagt es die Maske).
 * Reiner Text, der keine Adresse ist, wird stillschweigend übergangen.
 */
export function adressAblageflaeche(setAktiv: (a: boolean) => void, ablegen: (adresse: string | null) => void) {
  return {
    onDragOver: (e: React.DragEvent) => {
      const art = ziehtAdresse(e.dataTransfer)
      if (!art || imFeld(e.target)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
      if (art === 'adresse') setAktiv(true)
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      setAktiv(false)
    },
    onDrop: (e: React.DragEvent) => {
      const art = ziehtAdresse(e.dataTransfer)
      if (!art || imFeld(e.target)) return
      e.preventDefault()
      e.stopPropagation()
      setAktiv(false)
      const nurText = art === 'vielleicht' && !typen(e.dataTransfer).includes('Files')
      void adresseAusAblage(e.dataTransfer).then((adresse) => {
        if (adresse || !nurText) ablegen(adresse)
      })
    },
  }
}

/**
 * Einmal im Layout: merkt sich Ziehen, das in der Anwendung selbst beginnt, und fängt eine Adresse ab, die
 * **neben** eine Ablagefläche fällt — der Browser öffnete sie sonst in diesem Tab, und die Maske wäre weg.
 */
export function useAdressAblageSchutz() {
  useEffect(() => {
    const beginn = () => {
      intern = true
    }
    const ende = () => {
      intern = false
    }
    const daneben = (e: DragEvent) => {
      // Wer das Ablegen annimmt, hat `preventDefault` schon gerufen — dann ist es nicht daneben.
      if (e.defaultPrevented || intern || !traegtAdresse(e.dataTransfer) || typen(e.dataTransfer).includes('Files') || imFeld(e.target)) return
      e.preventDefault()
      if (e.type === 'dragover' && e.dataTransfer) e.dataTransfer.dropEffect = 'none'
    }
    window.addEventListener('dragstart', beginn)
    window.addEventListener('dragend', ende)
    window.addEventListener('dragover', daneben)
    window.addEventListener('drop', daneben)
    return () => {
      intern = false
      window.removeEventListener('dragstart', beginn)
      window.removeEventListener('dragend', ende)
      window.removeEventListener('dragover', daneben)
      window.removeEventListener('drop', daneben)
    }
  }, [])
}

// ── Übergabe vom Menüpunkt an die Seite ─────────────────────────────────────

/** Darf dieser Benutzer Geschäftspartner anlegen? Dieselbe Frage wie der Knopf auf der Seite. */
export function darfAdresseAblegen(): boolean {
  const { user, hasModule } = useAuthStore.getState()
  return hasModule('CRM') && erfuelltRecht('crm.client.manage', user?.permissions)
}

interface AdressAblageState {
  /** Auf dem Menüpunkt abgelegt und noch nicht abgeholt; `adresse: null` = es war keine Adresse */
  wartend: { adresse: string | null } | null
  lege: (adresse: string | null) => void
  /** Holt die wartende Adresse ab — genau einmal. */
  nimm: () => { adresse: string | null } | null
}

export const useAdressAblage = create<AdressAblageState>((set, get) => ({
  wartend: null,
  lege: (adresse) => set({ wartend: { adresse } }),
  nimm: () => {
    const w = get().wartend
    if (w) set({ wartend: null })
    return w
  },
}))
